import type { GatewayError } from '@/utils/gatewayError'
import * as React from 'react'
import { Box } from '@mui/material'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import type { PeriodOption } from '@/services/consumerService'
import type { DateRange as PeriodicDateRange } from './filter-partial/PeriodicFilter'
import { getDefaultFifteenthDateRange } from '@/utils/consumerReadingsDateRanges'

import hourlyColumnsConfig from '@/components/features/config/columns/consumers/HourlyFilter.column.config'
import dailyColumnsConfig from '@/components/features/config/columns/consumers/DailyFilter.column.config'
import periodicColumnsConfig from '@/components/features/config/columns/consumers/PeriodicFilter.column.config'
import detailedColumnsConfig from '@/components/features/config/columns/consumers/DetailedFilter.column.config'

import { hourlyRows } from '@/mockdata/hourlyData'
import { dailyRows } from '@/mockdata/dailyData'
import { detailedRows } from '@/mockdata/detailedData'

import Filters from './Filters'
import type {
  GridApiPremium,
  GridRowId,
  GridSortModel,
} from '@mui/x-data-grid-premium'
import { filtersTypes } from './types'
import { CONSUMPTION_FIELD } from '@/components/features/config/columns/consumers/column.config'
import columnsWithConsumption from '@/components/features/config/columns/consumers/calls.column.config'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import { debugLog } from '@/utils/debugLog'

const getPeriodMonths = (period: string) => {
  const normalized = period.toUpperCase().replace('-', '_')
  return normalized === 'MONTHLY'
    ? 1
    : normalized === 'QUARTERLY'
      ? 3
      : normalized === 'SEMI_ANNUAL' || normalized === 'HALF_YEAR'
        ? 6
        : normalized === 'ANNUAL' || normalized === 'YEARLY'
          ? 12
          : 2
}

const parsePeriodicDate = (value: unknown) => {
  if (!value) return null

  const candidate = String(value).split(/\s+-\s+/)[0].trim()
  const dayMonthYear = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(candidate)
  const normalized = dayMonthYear
    ? `${dayMonthYear[3]}-${dayMonthYear[2]}-${dayMonthYear[1]}`
    : candidate
  const parsed = dayjs(normalized)

  return parsed.isValid() ? parsed.startOf('day') : null
}

const getPeriodicRowStart = (row: any) =>
  parsePeriodicDate(
    row && typeof row === 'object'
      ? row.period ??
        row.periodStart ??
        row.period_start ??
        row.startTimestamp ??
        row.date ??
        row.periodRange
      : row
  )

const getPeriodicRowEnd = (start: dayjs.Dayjs, period: string) =>
  start.add(getPeriodMonths(period), 'month').subtract(1, 'day')

const getPeriodicRowEndDate = (
  row: any,
  start: dayjs.Dayjs | null,
  period: string
) => {
  const backendEnd = parsePeriodicDate(
    row && typeof row === 'object'
      ? row.periodEnd ?? row.period_end ?? row.endDate ?? row.end_date
      : null
  )
  return backendEnd ?? (start ? getPeriodicRowEnd(start, period) : null)
}

const isPeriodicRowCompleted = (row: any, period: string) => {
  const start = getPeriodicRowStart(row)
  if (!start) return true

  const end = getPeriodicRowEndDate(row, start, period)
  return !end || end.endOf('day').isBefore(dayjs())
}

const isPeriodicRowInRange = (
  row: any,
  period: string,
  range: PeriodicDateRange
) => {
  const start = getPeriodicRowStart(row)
  const selectedFrom = range.from ? dayjs(range.from).startOf('day') : null
  const selectedTo = range.to ? dayjs(range.to).endOf('day') : null

  if (!start || !selectedFrom?.isValid() || !selectedTo?.isValid()) return false

  const end = getPeriodicRowEndDate(row, start, period)?.endOf('day')
  if (!end) return false
  return !end.isBefore(selectedFrom) && !start.isAfter(selectedTo)
}

const formatPeriodRange = (
  value: unknown,
  period: string,
  range?: PeriodicDateRange
) => {
  const rawStart = getPeriodicRowStart(value)
  if (!rawStart) return '-'

  const selectedStart = range?.from ? dayjs(range.from).startOf('day') : null
  const selectedEnd = range?.to ? dayjs(range.to).endOf('day') : null
  const start =
    selectedStart?.isValid() && rawStart.isBefore(selectedStart)
      ? selectedStart
      : rawStart
  const calculatedEnd = getPeriodicRowEndDate(value, rawStart, period)
  if (!calculatedEnd) return '-'
  const end =
    selectedEnd?.isValid() && calculatedEnd.isAfter(selectedEnd)
      ? selectedEnd
      : calculatedEnd

  return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`
}

const getDefaultPeriodicRange = (): PeriodicDateRange => getDefaultFifteenthDateRange()

function useGridConfigByType(type: filtersTypes) {
  switch (type) {
    case filtersTypes.hourly:
      return {
        columns: hourlyColumnsConfig,
        rows: hourlyRows as any[],
        consumptionField: null as string | null,
      }
    case filtersTypes.daily:
      return {
        columns: dailyColumnsConfig,
        rows: dailyRows as any[],
        consumptionField: 'consumption',
      }
    case filtersTypes.periodic:
      return {
        columns: periodicColumnsConfig,
        rows: [],
        consumptionField: 'consumption',
      }
    case filtersTypes.detailed:
      return {
        columns: detailedColumnsConfig,
        rows: detailedRows as any[],
        consumptionField: null,
      }
    default:
      // fallback (ניתן להשאיר ריק)
      return {
        columns: columnsWithConsumption,
        rows: [],
        consumptionField: CONSUMPTION_FIELD,
      }
  }
}

type Props = {
  type: filtersTypes
  /** Shown in place of the grid, for example when the tab's request failed. */
  gridNotice?: React.ReactNode
  pageSize?: number
  onPageSizeChange?: (pageSize: number) => void
  onSortChange?: (field: string, order: 'ASC' | 'DESC') => void
  sortModel?: GridSortModel
  hourlyData: any[]
  dailyData: any[]
  dailyChartData?: any[]
  hourlyLoading?: boolean
  hourlyTotal?: number
  hourlyAvg?: number
  onHourlySearch?: (from?: string, to?: string) => void
  hourlyRange?: { from: Date | null; to: Date | null }
  onHourlyRangeChange?: (range: { from: Date | null; to: Date | null }) => void
  hourlyRowCount?: number
  hourlyPage?: number
  onHourlyPageChange?: (page: number, pageSize?: number) => void
  dailyLoading?: boolean
  dailyChartLoading?: boolean
  dailyChartError?: GatewayError | null
  dailyTotal?: number
  dailyAvg?: number
  dailyNightlyTotal?: number
  dailyNightlyAvg?: number
  dailyMin?: {
    value: number
    day: string
    pageNumber: number
    totalPages: number
    totalElements: number
    pageRows: any[] | null
  } | null
  dailyMax?: {
    value: number
    day: string
    pageNumber: number
    totalPages: number
    totalElements: number
    pageRows: any[] | null
  } | null
  onDailySearch?: (from?: string, to?: string) => void
  dailyRange?: { from: Date | null; to: Date | null }
  onDailyRangeChange?: (range: { from: Date | null; to: Date | null }) => void
  onDailyChartWeekChange?: (from: string, to: string) => void
  dailyRowCount?: number
  dailyPage?: number
  onDailyPageChange?: (page: number, pageSize?: number) => void
  // Periodic props
  periodicData?: any[]
  periodicLoading?: boolean
  periodicTotal?: number
  periodicAvg?: number
  periodicChart?: { currentYear: number[]; yearAgo: number[] | null }
  onPeriodicSearch?: (
    from?: string,
    to?: string,
    periodOption?: PeriodOption
  ) => void
  periodicRange?: PeriodicDateRange
  onPeriodicRangeChange?: (range: PeriodicDateRange) => void
  periodicRowCount?: number
  periodicPage?: number
  onPeriodicPageChange?: (page: number, pageSize?: number) => void
  // Detailed props
  detailedData?: any[]
  detailedLoading?: boolean
  onDetailedSearch?: (from?: string, to?: string) => void
  detailedRange?: { from: Date | null; to: Date | null }
  onDetailedRangeChange?: (range: { from: Date | null; to: Date | null }) => void
  detailedRowCount?: number
  detailedPage?: number
  onDetailedPageChange?: (page: number, pageSize?: number) => void
}

export default function ConsumerCallsContent({
  type,
  gridNotice,
  pageSize = 50,
  onPageSizeChange,
  onSortChange,
  sortModel,
  hourlyData,
  dailyData,
  dailyChartData,
  hourlyLoading,
  hourlyTotal,
  hourlyAvg,
  onHourlySearch,
  hourlyRange,
  onHourlyRangeChange,
  hourlyRowCount,
  hourlyPage,
  onHourlyPageChange,
  dailyLoading,
  dailyChartLoading,
  dailyChartError,
  dailyTotal,
  dailyAvg,
  dailyNightlyTotal,
  dailyNightlyAvg,
  dailyMin,
  dailyMax,
  onDailySearch,
  dailyRange,
  onDailyRangeChange,
  onDailyChartWeekChange,
  dailyRowCount,
  dailyPage,
  onDailyPageChange,
  // Periodic props
  periodicData,
  periodicLoading,
  periodicTotal,
  periodicAvg,
  periodicChart,
  onPeriodicSearch,
  periodicRange: periodicRangeProp,
  onPeriodicRangeChange,
  periodicRowCount,
  periodicPage,
  onPeriodicPageChange,
  // Detailed props
  detailedData,
  detailedLoading,
  onDetailedSearch,
  detailedRange,
  onDetailedRangeChange,
  detailedRowCount,
  detailedPage,
  onDetailedPageChange,
}: Props) {
  const gridApiRef = React.useRef<GridApiPremium | null>(null)
  const [localSelectedId, setLocalSelectedId] =
    React.useState<GridRowId | null>(null)
  const [periods, setPeriods] = React.useState<string>('bi-monthly')
  const [periodicRange, setPeriodicRange] = React.useState<PeriodicDateRange>(
    periodicRangeProp ?? getDefaultPeriodicRange()
  )
  const [appliedPeriodicRange, setAppliedPeriodicRange] =
    React.useState<PeriodicDateRange>(periodicRange)
  const { columns, rows, consumptionField } = useGridConfigByType(type)

  // Local overrides for daily data/pagination when jumping to a pre-loaded min/max page
  const [dailyDataOverride, setDailyDataOverride] = React.useState<
    any[] | null
  >(null)
  const [dailyPageOverride, setDailyPageOverride] = React.useState<
    number | null
  >(null)
  const [dailyRowCountOverride, setDailyRowCountOverride] = React.useState<
    number | null
  >(null)
  // Clear overrides when parent pushes new daily data (e.g. user navigates or searches)
  React.useEffect(() => {
    setDailyDataOverride(null)
    setDailyPageOverride(null)
    setDailyRowCountOverride(null)
  }, [dailyData])

  // Display rows — sort by sortModel (handles both server and client-side columns like dayOfWeek / alerts)
  const sortedDisplayRows = React.useMemo(() => {
    const source =
      type === filtersTypes.hourly
        ? hourlyData
        : type === filtersTypes.daily
          ? (dailyDataOverride ?? dailyData)
          : type === filtersTypes.periodic
            ? periodicData
            : type === filtersTypes.detailed
              ? detailedData
              : rows

    let currentRows: any[] = []
    if (type !== filtersTypes.periodic) {
      currentRows = Array.isArray(source) ? [...source] : (Array.isArray(rows) ? [...rows] : [])
    } else {
      const selectedPeriod = periods || 'bi-monthly'
      const sourceRows = Array.isArray(source) ? source : []
      const rowsInSelectedRange = sourceRows.filter((row: any) =>
        isPeriodicRowInRange(row, selectedPeriod, appliedPeriodicRange) &&
        isPeriodicRowCompleted(row, selectedPeriod)
      )
      currentRows = rowsInSelectedRange.map((row: any) => ({
        ...row,
        periodRange: formatPeriodRange(
          row,
          selectedPeriod,
          appliedPeriodicRange
        ),
      }))
    }

    if (!sortModel || sortModel.length === 0 || !sortModel[0].field || !sortModel[0].sort) {
      return currentRows
    }

    const { field, sort } = sortModel[0]
    const isAsc = sort === 'asc'
    const col = columns.find(c => c.field === field)
    const comparator = col?.sortComparator

    return currentRows.sort((a, b) => {
      if (comparator) {
        const valA = a?.[field]
        const valB = b?.[field]
        const res = comparator(valA, valB, a, b)
        return isAsc ? res : -res
      }
      if (field === 'dayOfWeek') {
        const tsA = a?.timestamp ?? a?.date ?? a?.day
        const tsB = b?.timestamp ?? b?.date ?? b?.day
        const dayA = tsA ? dayjs(tsA).day() : -1
        const dayB = tsB ? dayjs(tsB).day() : -1
        return isAsc ? dayA - dayB : dayB - dayA
      }
      if (field === 'alerts') {
        const rawA = a?.alerts || a?.alert
        const rawB = b?.alerts || b?.alert
        const sA = Array.isArray(rawA) ? rawA.join(', ') : String(rawA ?? '')
        const sB = Array.isArray(rawB) ? rawB.join(', ') : String(rawB ?? '')
        return isAsc ? sA.localeCompare(sB, 'he') : sB.localeCompare(sA, 'he')
      }
      const valA = a?.[field]
      const valB = b?.[field]
      if (typeof valA === 'number' && typeof valB === 'number') {
        return isAsc ? valA - valB : valB - valA
      }
      return isAsc
        ? String(valA ?? '').localeCompare(String(valB ?? ''))
        : String(valB ?? '').localeCompare(String(valA ?? ''))
    })
  }, [
    type,
    hourlyData,
    dailyData,
    dailyDataOverride,
    periodicData,
    detailedData,
    rows,
    periods,
    appliedPeriodicRange,
    sortModel,
    columns,
  ])

  const periodicRowsWereFiltered =
    type === filtersTypes.periodic &&
    (periodicData?.length ?? 0) !== sortedDisplayRows.length

  React.useEffect(() => {
    if (type !== filtersTypes.periodic || !import.meta.env.DEV) return

    const apiRows = periodicData?.length ?? 0
    debugLog('[PERIODIC DEBUG] UI filtering result:', {
      rowsFromApi: apiRows,
      rowsDisplayed: sortedDisplayRows.length,
      selectedPeriod: periods || 'bi-monthly',
      selectedRange: {
        from: appliedPeriodicRange.from,
        to: appliedPeriodicRange.to,
      },
      firstApiRows: periodicData?.slice(0, 5) ?? [],
    })

    if (apiRows > 0 && sortedDisplayRows.length === 0) {
      console.warn(
        '[PERIODIC DEBUG] The API returned rows, but the selected date/period filter removed all of them.'
      )
    }
  }, [
    type,
    periodicData,
    sortedDisplayRows,
    periods,
    appliedPeriodicRange,
  ])
  const displayedPeriodicTotal = React.useMemo(
    () =>
      sortedDisplayRows.reduce(
        (total: number, row: any) => total + Number(row.consumption ?? 0),
        0
      ),
    [sortedDisplayRows]
  )
  const displayedPeriodicAverage = sortedDisplayRows.length
    ? displayedPeriodicTotal / sortedDisplayRows.length
    : 0

  const handlePeriodicSearch = React.useCallback(
    (from?: string, to?: string, periodOption?: PeriodOption) => {
      if (from && to) {
        const nextFrom = dayjs(from).startOf('day')
        const nextTo = dayjs(to).startOf('day')
        if (nextFrom.isValid() && nextTo.isValid() && !nextFrom.isAfter(nextTo)) {
          setAppliedPeriodicRange({
            from: nextFrom.toDate(),
            to: nextTo.toDate(),
          })
        }
      }
      onPeriodicSearch?.(from, to, periodOption)
    },
    [onPeriodicSearch]
  )

  // State for pending highlight after page/data loads
  const [pendingHighlightDate, setPendingHighlightDate] = React.useState<
    string | null
  >(null)
  // Pre-loaded rows from min/max page data (avoids re-fetching)
  const [pendingHighlightRows, setPendingHighlightRows] = React.useState<
    any[] | null
  >(null)

  // State for controlled page navigation (for min/max jump)
  const [controlledPage, setControlledPage] = React.useState<
    number | undefined
  >(undefined)

  // Use sorted data for min/max calculations
  const dataForMinMax = React.useMemo(() => {
    if (sortedDisplayRows.length > 0) return sortedDisplayRows
    return rows
  }, [sortedDisplayRows, rows])

  const { minRow, maxRow, minVal, maxVal } = React.useMemo(() => {
    if (!dataForMinMax.length || !consumptionField) {
      return {
        minRow: null as any,
        maxRow: null as any,
        minVal: null as number | null,
        maxVal: null as number | null,
      }
    }
    let min = dataForMinMax[0]
    let max = dataForMinMax[0]
    for (const r of dataForMinMax) {
      const v = Number(r[consumptionField] ?? 0)
      if (Number(min[consumptionField] ?? 0) > v) min = r
      if (Number(max[consumptionField] ?? 0) < v) max = r
    }
    return {
      minRow: min,
      maxRow: max,
      minVal: Number(min[consumptionField] ?? 0),
      maxVal: Number(max[consumptionField] ?? 0),
    }
  }, [dataForMinMax, consumptionField])

  const jumpToCell = (rowId: GridRowId, field: string) => {
    const api = gridApiRef.current as any
    if (!api) return

    // Use sorted data (same order as DataGrid)
    const allIds: GridRowId[] = sortedDisplayRows.map(
      (r: any) => r.id as GridRowId
    )
    const absoluteIndex = allIds.indexOf(rowId)
    if (absoluteIndex === -1) {
      debugLog('[jumpToCell] Row not found in data:', rowId)
      return
    }

    const pageSize =
      api.state?.pagination?.paginationModel?.pageSize ??
      api.state?.pagination?.pageSize ??
      10

    const targetPage = Math.floor(absoluteIndex / pageSize)
    const indexOnPage = absoluteIndex - targetPage * pageSize

    debugLog('[jumpToCell] Navigating to:', {
      rowId,
      absoluteIndex,
      targetPage,
      indexOnPage,
      pageSize,
    })

    // Change to target page using controlledPage state
    setControlledPage(targetPage)

    // Wait for page change to render, then scroll and select
    setTimeout(() => {
      const colIndex =
        typeof api.getColumnIndex === 'function'
          ? api.getColumnIndex(field)
          : api.getAllColumns().findIndex((c: any) => c.field === field)

      let rowIndex = -1
      if (typeof api.getRowIndexRelativeToVisibleRows === 'function') {
        try {
          rowIndex = api.getRowIndexRelativeToVisibleRows(rowId)
        } catch {}
      }
      if (rowIndex === -1 || rowIndex == null) rowIndex = indexOnPage

      try {
        api.scrollToIndexes({ rowIndex, colIndex })
        debugLog('[jumpToCell] Scrolled to:', { rowIndex, colIndex })
      } catch (e) {
        console.error('[jumpToCell] scrollToIndexes error:', e)
      }
      try {
        setLocalSelectedId(rowId)
        debugLog('[jumpToCell] Selected row:', rowId)
      } catch {}
    }, 150) // Wait for page change animation
  }

  // Jump to a min/max row — uses pre-loaded page data from the API to avoid extra requests
  const jumpToMinMax = (
    info:
      | {
          day: string
          pageNumber: number
          totalElements: number
          pageRows: any[] | null
        }
      | null
      | undefined,
    fallbackRow: any
  ) => {
    if (type !== filtersTypes.daily) return

    if (info?.day) {
      // 1. Try to find in current page data
      const targetRow = sortedDisplayRows.find(
        (row: any) => row.date === info.day || row.timestamp === info.day
      )
      if (targetRow) {
        jumpToCell(targetRow.id, consumptionField || 'consumption')
        return
      }

      // 2. Row is on a different page — swap in pre-loaded rows (no backend call)
      if (info.pageRows && info.pageRows.length > 0) {
        setDailyDataOverride(info.pageRows)
        setDailyPageOverride(info.pageNumber - 1) // 0-indexed for DataGrid
        setDailyRowCountOverride(info.totalElements)
        setControlledPage(0) // reset DataGrid internal page to 0 (single page of data)
        setPendingHighlightDate(info.day)
        setPendingHighlightRows(info.pageRows)
        return
      }
    }

    // Fallback to local calculation
    if (fallbackRow && consumptionField) {
      jumpToCell(fallbackRow.id, consumptionField)
    }
  }

  const onJumpMin = () => jumpToMinMax(dailyMin, minRow)
  const onJumpMax = () => jumpToMinMax(dailyMax, maxRow)

  // Auto-highlight row when pending pre-loaded rows arrive (from min/max jump)
  useEffect(() => {
    if (!pendingHighlightDate || type !== filtersTypes.daily) return

    // Use pre-loaded rows if available (no backend call needed)
    const dataToSearch = pendingHighlightRows ?? sortedDisplayRows
    if (dataToSearch.length === 0) return

    const targetRow = dataToSearch.find(
      (row: any) =>
        row.date === pendingHighlightDate ||
        row.timestamp === pendingHighlightDate
    )
    if (targetRow) {
      setTimeout(() => {
        jumpToCell(targetRow.id, consumptionField || 'consumption')
        setPendingHighlightDate(null)
        setPendingHighlightRows(null)
      }, 100)
    }
  }, [
    sortedDisplayRows,
    pendingHighlightDate,
    pendingHighlightRows,
    type,
    consumptionField,
  ])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column-reverse', lg: 'row' },
        gap: { xs: 2, lg: 2, xl: 3 },
        alignItems: 'stretch',
        width: '100%',
        minWidth: 0,
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, width: '100%', minHeight: 0 }}>
        {gridNotice ?? (
          <DataGridPremiumDemo
            columns={columns}
            rows={sortedDisplayRows}
            loading={
              (type === filtersTypes.hourly && hourlyLoading) ||
              (type === filtersTypes.daily && dailyLoading) ||
              (type === filtersTypes.periodic && periodicLoading) ||
              (type === filtersTypes.detailed && detailedLoading) ||
              false
            }
            headerPosition="bottom"
            pageSize={pageSize}
            apiRefExternal={gridApiRef}
            selectedRowId={localSelectedId}
            onSelectedRowIdChange={setLocalSelectedId}
            width="100%"
            controlledPage={controlledPage}
            sortingMode="server"
            onSortChange={onSortChange}
            externalSortModel={sortModel}
            paginationMode="server"
            serverSideRowCount={
              type === filtersTypes.hourly
                ? (hourlyRowCount || sortedDisplayRows.length)
                : type === filtersTypes.daily
                  ? ((dailyRowCountOverride ?? dailyRowCount) || sortedDisplayRows.length)
                  : type === filtersTypes.periodic
                    ? periodicRowsWereFiltered
                      ? sortedDisplayRows.length
                      : (periodicRowCount || sortedDisplayRows.length)
                    : type === filtersTypes.detailed
                      ? (detailedRowCount || sortedDisplayRows.length)
                      : 0
            }
            serverSidePage={
              type === filtersTypes.hourly
                ? (hourlyPage ?? 0)
                : type === filtersTypes.daily
                  ? (dailyPageOverride ?? dailyPage ?? 0)
                  : type === filtersTypes.periodic
                    ? (periodicPage ?? 0)
                    : type === filtersTypes.detailed
                      ? (detailedPage ?? 0)
                      : 0
            }
            onPaginationChange={(newPage: number, newPageSize?: number) => {
              // Clear min/max override when user navigates manually
              if (dailyDataOverride) {
                setDailyDataOverride(null)
                setDailyPageOverride(null)
                setDailyRowCountOverride(null)
              }
              if (newPageSize && newPageSize !== pageSize && onPageSizeChange) {
                onPageSizeChange(newPageSize)
              }
              if (type === filtersTypes.hourly && onHourlyPageChange)
                onHourlyPageChange(newPage, newPageSize)
              else if (type === filtersTypes.daily && onDailyPageChange)
                onDailyPageChange(newPage, newPageSize)
              else if (type === filtersTypes.periodic && onPeriodicPageChange)
                onPeriodicPageChange(newPage, newPageSize)
              else if (type === filtersTypes.detailed && onDetailedPageChange)
                onDetailedPageChange(newPage, newPageSize)
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          width: { xs: '100%', lg: 'clamp(420px, 34vw, 640px)' },
          flexShrink: 0,
          position: { lg: 'sticky' },
          top: { lg: 16 },
          maxHeight: { lg: 'calc(100dvh - 220px)' },
          overflowY: { lg: 'auto' },
        }}
      >
        <Filters
          type={type}
          dailyMinValue={
            type === filtersTypes.daily
              ? (dailyMin?.value ?? minVal ?? null)
              : null
          }
          dailyMaxValue={
            type === filtersTypes.daily
              ? (dailyMax?.value ?? maxVal ?? null)
              : null
          }
          onDailyJumpMin={type === filtersTypes.daily ? onJumpMin : undefined}
          onDailyJumpMax={type === filtersTypes.daily ? onJumpMax : undefined}
          onPeriodsChange={setPeriods}
          hourlyLoading={hourlyLoading}
          hourlyTotal={hourlyTotal}
          hourlyAvg={hourlyAvg}
          onHourlySearch={onHourlySearch}
          hourlyRange={hourlyRange}
          onHourlyRangeChange={onHourlyRangeChange}
          dailyData={dailyData}
          dailyChartData={dailyChartData}
          dailyChartLoading={dailyChartLoading}
          dailyChartError={dailyChartError}
          dailyLoading={dailyLoading}
          dailyTotal={dailyTotal}
          dailyAvg={dailyAvg}
          dailyNightlyTotal={dailyNightlyTotal}
          dailyNightlyAvg={dailyNightlyAvg}
          onDailySearch={onDailySearch}
          dailyRange={dailyRange}
          onDailyRangeChange={onDailyRangeChange}
          onDailyChartWeekChange={onDailyChartWeekChange}
          periodicData={
            type === filtersTypes.periodic ? sortedDisplayRows : periodicData
          }
          periodicLoading={periodicLoading}
          periodicTotal={
            periodicRowsWereFiltered ? displayedPeriodicTotal : periodicTotal
          }
          periodicAvg={
            periodicRowsWereFiltered ? displayedPeriodicAverage : periodicAvg
          }
          periodicChart={
            periodicRowsWereFiltered ? undefined : periodicChart
          }
          onPeriodicSearch={handlePeriodicSearch}
          periodicRange={periodicRangeProp ?? periodicRange}
          onPeriodicRangeChange={onPeriodicRangeChange ?? setPeriodicRange}
          detailedData={detailedData}
          detailedLoading={detailedLoading}
          onDetailedSearch={onDetailedSearch}
          detailedRange={detailedRange}
          onDetailedRangeChange={onDetailedRangeChange}
        />
      </Box>
    </Box>
  )
}
